/**
 * Form for editing invitation details within InformationOverlay.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { FormikWrapper, FormTextField, Button } from '@uncinc/uncinc-react-kitchen-sink';
import { useDialogDrawerContext } from '../../../../../context/DialogDrawerContext';

import StatusIndicator from '../StatusIndicator';
import type { ElearningInvitation } from '../../../../../types/orders';

import './style.scss';

interface InvitationEditFormProps {
  invitation: ElearningInvitation;
  onSubmit: (email: string) => Promise<boolean | void>;
  onDelete?: () => Promise<void>;
  errorMessage?: string | null;
}

const InvitationEditForm: React.FC<InvitationEditFormProps> = ({
  invitation,
  onSubmit,
  onDelete,
  errorMessage,
}) => {
  const { t } = useTranslation();
  const { closeDialog } = useDialogDrawerContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const canEdit = invitation.status === 'invited';

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email(t('mijn_vnl.orders.email_invalid'))
      .required(t('mijn_vnl.orders.email_required')),
  });

  const handleSubmit = async (values: { email: string }) => {
    try {
      setIsSubmitting(true);
      const result = await onSubmit(values.email);
      // Only close dialog on success (result is true or void/undefined)
      if (result !== false) {
        closeDialog();
      }
    } catch {
      // Error occurred
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;

    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(t('mijn_vnl.orders.delete_confirmation'));
    if (!confirmed) return;

    try {
      setIsDeleting(true);
      await onDelete();
      closeDialog();
    } catch {
      setIsDeleting(false);
    }
  };

  return (
    <div className="InvitationEditForm">
      <FormikWrapper
        initialValues={{ email: invitation.email }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <div className="InvitationEditForm__content">
          <div className="InvitationEditForm__status">
            Status: <StatusIndicator status={invitation.status} />
          </div>

          {invitation.status === 'invited' && (
            <p className="InvitationEditForm__status-description">
              {t('mijn_vnl.orders.status_description_invited')}
            </p>
          )}

          {invitation.status === 'started' && (
            <p className="InvitationEditForm__status-description">
              {t('mijn_vnl.orders.status_description_started')}
            </p>
          )}

          <div className="InvitationEditForm__email-field">
            <FormTextField
              title={t('mijn_vnl.orders.invite_email_label')}
              fieldName="email"
              type="email"
              disabled={!canEdit}
            />
          </div>

          {invitation.status === 'started' && invitation.progress_percentage !== undefined && (
            <div className="InvitationEditForm__progress">
              <p>{t('mijn_vnl.orders.progress')}: {invitation.progress_percentage}%</p>
            </div>
          )}
        </div>

        {errorMessage && (
          <div className="InvitationEditForm__error" role="alert">
            <p>{errorMessage}</p>
          </div>
        )}

        <div className="InvitationEditForm__actions">
          <Button
            type="submit"
            className="Button--primary"
            disabled={!canEdit || isSubmitting}
          >
            {isSubmitting ? t('general.loading') : t('general.save')}
          </Button>

          {onDelete && canEdit && (
            <Button
              type="button"
              className="Button Button--danger"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? t('general.loading') : t('mijn_vnl.orders.delete_invitation')}
            </Button>
          )}
        </div>
      </FormikWrapper>
    </div>
  );
};

export default InvitationEditForm;
